import { useState } from "react"

const WORKER_URL = (import.meta.env.VITE_WORKER_URL ?? "").replace(/\/$/, "")

async function apiFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${WORKER_URL}/admin${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(opts?.headers ?? {}) },
  })
  if (!res.ok) throw new Error(`API error: ${res.status}`)
  return res.json() as Promise<T>
}

export interface AdminCompany {
  id: string
  name: string
  nameKana: string
  industry: string
  officialUrl: string | null
  crawlStatus: string | null
  lastCrawledAt: number | null
  errorCount: number
  lastError: string | null
  retryExhausted: boolean
  noUrl: boolean
  noBenefit: boolean
  isDiscontinued: boolean
  discontinuedYear: number | null
  discontinuedMonth: number | null
  urlSearchFailedAt: number | null
  needsReview: boolean
  businessDescriptionUnclear: boolean
  benefitActive: boolean
  benefitTitle: string | null
  benefitDescription: string | null
  businessDescription: string | null
  minimumShares: number | null
  minimumBenefitValue: number | null
  holdingYearsJson: string | null
  benefitLastChangedAt: number | null
  benefitCategories: string | null
  benefitRecordMonths: string | null
  benefitDeliveryMonths: string | null
  benefitTiersJson: string | null
  benefitRelevanceScore: number | null
  confirmedYearsJson: string | null
  approxStockPrice: number | null
  marketCap: number | null
  dividendYield: number | null
  financialsSyncedAt: number | null
  fiscalPeriodEnd: string | null
}

export interface CrawlDebugResult {
  officialUrl: string
  fetchError: string | null
  looksBlocked: boolean
  extractedText: string | null
  extractedTextLength: number | null
  aiExtraction: {
    title: string
    description: string
    categories: string[]
    recordMonths: number[]
    deliveryMonths: number[]
    minimumShares: number
    benefitTiers: { minimumShares: number; description: string }[]
    noBenefit?: boolean
    isDiscontinued?: boolean
    discontinuedYear?: number | null
    discontinuedMonth?: number | null
    minimumBenefitValue?: number | null
    holdingYears?: number[]
    hasMultipleConditions?: boolean
  } | null
  aiUsage: {
    inputTokens: number
    outputTokens: number
    cacheReadTokens: number
    cacheCreationTokens: number
  } | null
  aiFailed: boolean
}

export const PAGE_SIZE = 500

export interface JobEvent {
  id: string
  jobType: string
  companyId: string | null
  companyName: string | null
  status: string
  message: string
  createdAt: number
  officialUrl: string | null
}

export type JobEventTypeFilter = "" | "url_discovery" | "crawl" | "edinet_sync"

export function useJobEvents() {
  const [events, setEvents] = useState<JobEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [jobType, setJobType] = useState<JobEventTypeFilter>("")

  async function load(type: JobEventTypeFilter = jobType) {
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams({ limit: "100" })
      if (type) qs.set("jobType", type)
      const data = await apiFetch<{ items: JobEvent[] }>(`/events?${qs}`)
      setEvents(data.items)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }

  function changeType(type: JobEventTypeFilter) {
    setJobType(type)
    load(type)
  }

  return { events, loading, error, jobType, load, changeType }
}

export interface AdminStats {
  total: number
  withUrl: number
  benefitAcquired: number
  errors: number
  searchFailed: number
  notCrawled: number
  noBenefit: number
  flagNoUrl: number
  discontinued: number
  needsReview: number
  businessDescriptionUnclear: number
  edinetFetched: number
  edinetUnavailable: number
  edinetErrors: number
}

export type AdminFilter =
  | "all"
  | "yes"
  | "no"
  | "error"
  | "searchFailed"
  | "benefitAcquired"
  | "notCrawled"
  | "noBenefit"
  | "flagNoUrl"
  | "discontinued"
  | "needsReview"
  | "businessDescriptionUnclear"

export function useAdminStats() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await apiFetch<AdminStats>("/stats")
      setStats(data)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }

  function adjustStats(delta: Partial<Record<keyof AdminStats, number>>) {
    setStats((prev) => {
      if (!prev) return prev
      const next = { ...prev }
      for (const key of Object.keys(delta) as (keyof AdminStats)[]) {
        next[key] = Math.max(0, next[key] + (delta[key] ?? 0))
      }
      return next
    })
  }

  return { stats, loading, error, load, adjustStats }
}

export function useAdminCompanies() {
  const [items, setItems] = useState<AdminCompany[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasUrlFilter, setHasUrlFilter] = useState<AdminFilter>("all")

  async function load(p = page, filter = hasUrlFilter) {
    setLoading(true)
    setError(null)
    try {
      const hasUrl = filter === "yes" ? "true" : filter === "no" ? "false" : ""
      const qs = new URLSearchParams({ page: String(p), limit: String(PAGE_SIZE) })
      if (hasUrl) qs.set("hasUrl", hasUrl)
      if (filter === "error") qs.set("crawlStatus", "error")
      if (filter === "notCrawled") qs.set("notCrawled", "true")
      if (filter === "searchFailed") qs.set("searchFailed", "true")
      if (filter === "benefitAcquired") qs.set("benefitAcquired", "true")
      if (filter === "noBenefit") qs.set("noBenefit", "true")
      if (filter === "flagNoUrl") qs.set("flagNoUrl", "true")
      if (filter === "discontinued") qs.set("discontinued", "true")
      if (filter === "needsReview") qs.set("needsReview", "true")
      if (filter === "businessDescriptionUnclear") qs.set("businessDescriptionUnclear", "true")
      const data = await apiFetch<{ items: AdminCompany[]; total: number }>(`/companies?${qs}`)
      setItems(data.items)
      setTotal(data.total)
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }

  function updateItemUrl(id: string, url: string) {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, officialUrl: url } : item)))
  }

  function updateItemFlag(
    id: string,
    flag: Partial<
      Pick<
        AdminCompany,
        "noUrl" | "noBenefit" | "isDiscontinued" | "needsReview" | "businessDescriptionUnclear"
      >
    >
  ) {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...flag } : item)))
  }

  function updateItem(id: string, partial: Partial<AdminCompany>) {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...partial } : item)))
  }

  async function fetchCompany(id: string): Promise<AdminCompany> {
    return apiFetch<AdminCompany>(`/companies/${id}`)
  }

  async function setUrl(id: string, url: string) {
    await apiFetch(`/companies/${id}/url`, {
      method: "PUT",
      body: JSON.stringify({ url }),
    })
    await load()
  }

  async function triggerCrawl(id: string) {
    await apiFetch(`/companies/${id}/crawl`, { method: "POST" })
  }

  async function fetchCrawlStatus(
    id: string
  ): Promise<{ crawlStatus: string | null; errorCount: number; lastError: string | null }> {
    return apiFetch(`/companies/${id}/crawl-status`)
  }

  async function debugCrawl(id: string): Promise<CrawlDebugResult> {
    return apiFetch(`/companies/${id}/crawl-debug`, { method: "POST" })
  }

  async function searchUrls(id: string): Promise<{
    results: { title: string; url: string }[]
    debug: string
    aiRecommended: string | null
    rawExaResponse?: string
  }> {
    return apiFetch<{
      results: { title: string; url: string }[]
      debug: string
      aiRecommended: string | null
      rawExaResponse?: string
    }>(`/companies/${id}/search-urls`, { method: "POST" })
  }

  async function discoverUrl(id: string): Promise<string | null> {
    const res = await apiFetch<{ ok: boolean; url: string | null }>(
      `/companies/${id}/discover-url`,
      { method: "POST" }
    )
    await load()
    return res.url
  }

  async function discoverUrlQuiet(id: string): Promise<string | null> {
    const res = await apiFetch<{ ok: boolean; url: string | null }>(
      `/companies/${id}/discover-url`,
      { method: "POST" }
    )
    return res.url
  }

  async function fetchCompaniesWithoutUrl(): Promise<AdminCompany[]> {
    const qs = new URLSearchParams({
      page: "1",
      limit: "1000",
      hasUrl: "false",
      excludeNoUrl: "true",
      excludeSearchFailed: "true",
    })
    const data = await apiFetch<{ items: AdminCompany[]; total: number }>(`/companies?${qs}`)
    return data.items
  }

  async function fetchCompaniesWithUrl(): Promise<AdminCompany[]> {
    const qs = new URLSearchParams({
      page: "1",
      limit: "2000",
      hasUrl: "true",
      notCrawled: "true",
    })
    const data = await apiFetch<{ items: AdminCompany[]; total: number }>(`/companies?${qs}`)
    return data.items
  }

  async function setFlags(
    id: string,
    flags: {
      noUrl?: boolean
      noBenefit?: boolean
      isDiscontinued?: boolean
      discontinuedYear?: number | null
      discontinuedMonth?: number | null
      needsReview?: boolean
      businessDescriptionUnclear?: boolean
    }
  ) {
    await apiFetch(`/companies/${id}/flags`, {
      method: "PUT",
      body: JSON.stringify(flags),
    })
  }

  async function cleanupNonIndividual(): Promise<number> {
    const res = await apiFetch<{ ok: boolean; deleted: number }>("/cleanup-non-individual", {
      method: "POST",
    })
    await load()
    return res.deleted
  }

  async function publishBenefits(): Promise<void> {
    await apiFetch("/publish", { method: "POST" })
  }

  async function triggerEdinetSync(): Promise<void> {
    await apiFetch("/edinet/trigger", { method: "POST" })
  }

  function changeFilter(f: AdminFilter) {
    setHasUrlFilter(f)
    setPage(1)
    load(1, f)
  }

  function changePage(p: number) {
    setPage(p)
    load(p)
  }

  return {
    items,
    total,
    page,
    loading,
    error,
    hasUrlFilter,
    load,
    setUrl,
    setFlags,
    searchUrls,
    discoverUrl,
    discoverUrlQuiet,
    fetchCompaniesWithoutUrl,
    fetchCompaniesWithUrl,
    updateItemUrl,
    updateItemFlag,
    updateItem,
    fetchCompany,
    triggerCrawl,
    fetchCrawlStatus,
    debugCrawl,
    cleanupNonIndividual,
    publishBenefits,
    triggerEdinetSync,
    changeFilter,
    changePage,
  }
}
